/* Report only complete, verified cart reads. This observer never changes the cart. */
(() => {
  if (globalThis.HeirloomCartState) return;
  const cartUrl = new URL((globalThis.Shopify?.routes?.root || '/') + 'cart.js', location.origin);
  if (cartUrl.origin !== location.origin) return;
  const endpoint = '/apps/3d-personalization/v1/cart-state';
  const guestEndpoint = '/apps/3d-personalization/v1/guest-cart-state';
  let signedInHere = false;
  document.addEventListener?.('heirloom:customer-authenticated', () => { signedInHere = true; });
  const guestReceipts = () => !signedInHere && document.body?.dataset?.customerLoggedIn === 'false' ? globalThis.HeirloomPersonalizationServer?.pending?.() || [] : [];
  const seen = new Map();
  let seenCart = '';
  let running = null, pending = false, blocked = false, lastStarted = 0, lastSent = '';
  const read = async (url, options = {}) => {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
    try {
      const result = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!result.ok) { if (url !== guestEndpoint && [401,403].includes(result.status)) blocked = true; return null; }
      return await result.json();
    } finally { clearTimeout(timer); }
  };
  function cartItems(cart) {
    if (!cart || typeof cart.token !== 'string' || !cart.token || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) return null;
    const items = []; let count = 0;
    for (const item of cart.items) {
      if (!item || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return null;
      count += item.quantity;
      const variant = item.variant_id ?? item.id, variantId = String(variant ?? '');
      if (!/^[0-9]+$/.test(variantId) || (typeof variant === 'number' && !Number.isSafeInteger(variant))) return null;
      const properties = item.properties ?? {};
      if (typeof properties !== 'object' || Array.isArray(properties)) return null;
      if (properties._parent_design_id) continue;
      const requestId = properties['_3D Heirloom Request'], designId = properties._design_id;
      if (!requestId && !designId) continue;
      if ((requestId && (typeof requestId !== 'string' || !/^3DH-[A-F0-9]{12}$/.test(requestId))) || (designId && (typeof designId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(designId)))) return null;
      if (item.quantity > 999) return null;
      items.push({ ...(requestId ? { requestId } : {}), ...(designId ? { designId } : {}), variantId, quantity: item.quantity });
      if (items.length > 100) return null;
    }
    return count === cart.item_count ? items : null;
  }
  async function reportGuest(cart, cartId, observedAt, receipts) {
    if (cartId !== seenCart) { seen.clear(); seenCart = cartId; }
    let sent = false;
    for (const { requestId, editToken } of receipts) {
      if (pending || signedInHere) break;
      if (!/^3DH-[A-F0-9]{12}$/.test(requestId) || typeof editToken !== 'string' || !editToken || editToken.length > 256) continue;
      const lines = cart.items.filter(({properties: p}) => p?.['_3D Heirloom Request'] === requestId && !p._parent_design_id && !p._linked_item_type);
      const previous = seen.get(requestId), first = lines[0];
      if (!first && !previous) continue;
      const revisionId = first ? first.properties['_3D Heirloom Revision'] : previous.revisionId;
      const variantId = first ? String(first.variant_id ?? first.id) : previous.variantId;
      if (!/^3DHR-[A-F0-9]{20}$/.test(revisionId) || !/^[1-9]\d{0,24}$/.test(variantId) || lines.some(line => line.properties['_3D Heirloom Revision'] !== revisionId || String(line.variant_id ?? line.id) !== variantId)) continue;
      const status = first ? 'present' : 'removed';
      if (previous?.ack === status && previous.revisionId === revisionId && previous.variantId === variantId) continue;
      const observation = { revisionId, variantId, ack: '' };
      seen.set(requestId, observation); // A lost response may already have committed.
      try {
        const result = await read(guestEndpoint, { method: 'POST', redirect: 'error', mode: 'same-origin', keepalive: true,
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'x-draft-token': editToken },
          body: JSON.stringify({requestId, revisionId, variantId, cartId, observedAt, status}) });
        if (result?.ok === true) { observation.ack = status; sent = true; }
      } catch { /* Retry only on a new confirmed cart read. */ }
    }
    return sent;
  }
  async function observe(snapshot) {
    const cart = snapshot === undefined ? await read(cartUrl.href, { headers: { Accept: 'application/json' } }) : snapshot;
    const observedAt = Date.now(), items = cartItems(cart);
    if (items === null || pending || !globalThis.crypto?.subtle) return false;
    // The raw token stays within this read and is never persisted or transmitted.
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(cart.token));
    const cartId = [...new Uint8Array(bytes)].map(byte => byte.toString(16).padStart(2,'0')).join('');
    const signature = JSON.stringify({ cartId, items });
    if (pending) return false;
    const body = JSON.stringify({ cartId, observedAt, items });
    if (new TextEncoder().encode(body).byteLength > 32768) return false;
    const guest = await reportGuest(cart, cartId, observedAt, guestReceipts());
    if (pending || blocked || signature === lastSent) return guest;
    // A lost response may still have committed; the next read must not reuse an older receipt.
    lastSent = '';
    const result = await read(endpoint, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body });
    if (result?.ok === true) { lastSent = signature; return true; }
    return guest;
  }
  function sync({ resume = false, force = false, snapshot } = {}) {
    if (document.visibilityState === 'hidden') return Promise.resolve(false);
    if (resume) {
      if (!force && !blocked && Date.now() - lastStarted < 15000) return running || Promise.resolve(false);
      blocked = false; lastSent = '';
      if (force || Date.now() - lastStarted >= 15000) seen.forEach(row => { row.ack = ''; });
    } else if (blocked && !guestReceipts().length) return Promise.resolve(false);
    if (running) { pending = true; return running; }
    running = (async () => {
      let result = false;
      do {
        pending = false; lastStarted = Date.now();
        try { result = await observe(snapshot); } catch { result = false; }
        snapshot = undefined;
      } while (pending && (!blocked || guestReceipts().length) && document.visibilityState !== 'hidden');
      return result;
    })().finally(() => { running = null; });
    return running;
  }
  async function guestCartChanged(url, snapshot) {
    try {
      const target = new URL(url, location.origin);
      if (target.href !== (snapshot === undefined ? cartUrl.href.replace(/cart\.js$/, 'cart/add.js') : cartUrl.href) || !guestReceipts().length) return false;
      return await sync({ snapshot });
    } catch { return false; } // Observations never block purchase or retry a cart mutation.
  }
  globalThis.HeirloomCartState = { sync, guestCartChanged };
  globalThis.addEventListener('focus', () => { void sync({ resume: true }); });
  globalThis.addEventListener('pageshow', event => { void sync({ resume: true, force: event.persisted === true }); });
  void sync();
})();
